import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import BASE_URL from '../../../config/config';
import { convertToJalali } from "../../../utils/dateConverter";

function isValidCardNumber(num) {
  if (num.length !== 16) return false;
  let sum = 0;
  for (let i = 0; i < 16; i++) {
    let n = parseInt(num.charAt(i));
    if (i % 2 === 0) {
      n *= 2;
      if (n > 9) n -= 9;
    }
    sum += n;
  }
  return sum % 10 === 0;
}

const EditOrder = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const jalaliToday = convertToJalali(new Date()).fullDate.split(" ")[0];

  const [formData, setFormData] = useState({
    user_id: '',
    course_id: '',
    status: 'pending',
    price: '',
    payment_method: '',
    service_type: 'register'
  });

  const [c2cData, setC2cData] = useState({
    paymentDate: '',
    cardNumber: '',
    trackingNumber: '',
    note: ''
  });

  const [zarinpalData, setZarinpalData] = useState(null);
  const [users, setUsers] = useState([]);
  const [courses, setCourses] = useState([]);
  const [csrfToken, setCsrfToken] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [errors, setErrors] = useState({});
  const [paymentSlipUrl, setPaymentSlipUrl] = useState(null);
  const [slipFile, setSlipFile] = useState(null);

  useEffect(() => {
    fetch(`${BASE_URL}/csrf-token`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => setCsrfToken(d.csrfToken));

    fetch(`${BASE_URL}/users?all=1`, {
      headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
      credentials: 'include'
    })
      .then(r => r.json())
      .then(d => setUsers(d.users || []));

    fetch(`${BASE_URL}/courses`, {
      headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
      credentials: 'include'
    })
      .then(r => r.json())
      .then(d => setCourses(d.data?.courses || []));
  }, []);

  useEffect(() => {
    if (!csrfToken) return;
    setFetchLoading(true);

    fetch(`${BASE_URL}/orders/${id}`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'CSRF-Token': csrfToken,
      },
      credentials: 'include',
    })
      .then(r => r.json())
      .then(orderRes => {
        const o = orderRes.data || {};
        
        // تشخیص روش پرداخت
        let payMethod = 'card_to_card';
        if (o.zarinpal_authority) payMethod = 'online';

        setFormData({
          user_id: o.user_id || '',
          course_id: o.course_id || '',
          status: o.status || 'pending',
          price: o.price || '',
          payment_method: payMethod,
          service_type: o.service_type || 'register'
        });

        // پارس description
        try {
          const descObj = JSON.parse(o.description || '{}');

          // اگه کارت‌به‌کارت بود
          if (descObj.cardNumber) {
            setC2cData({
              paymentDate: descObj.paymentDate || '',
              cardNumber: descObj.cardNumber || '',
              trackingNumber: descObj.trackingNumber || '',
              note: descObj.note || ''
            });
            if (descObj.paymentSlip) setPaymentSlipUrl(descObj.paymentSlip);
          }

          // اگه زرین‌پال بود
          if (descObj.ref_id) {
            setZarinpalData(descObj);
          }
        } catch {
          setC2cData({});
          setZarinpalData(null);
        }
      })
      .catch(() => setMessage('خطا در دریافت سفارش'))
      .finally(() => setFetchLoading(false));
  }, [csrfToken, id]);

  const validateField = (name, value) => {
    switch (name) {
      case 'user_id': return !value ? "انتخاب کاربر الزامی است" : "";
      case 'course_id': return !value ? "انتخاب دوره الزامی است" : "";
      case 'price':
        if (!value) return "مبلغ الزامی است";
        if (isNaN(value) || Number(value) <= 0) return "مبلغ نامعتبر است";
        return "";
      default: return "";
    }
  };

  const validateC2C = (name, value) => {
    switch (name) {
      case 'paymentDate':
        if (!value) return "تاریخ الزامی است";
        if (!/^\d{4}\/\d{2}\/\d{2}$/.test(value)) return "فرمت تاریخ نامعتبر";
        return "";
      case 'cardNumber':
        if (!value) return "شماره کارت الزامی است";
        if (!/^\d{16}$/.test(value)) return "شماره کارت باید 16 رقم باشد";
        if (!isValidCardNumber(value)) return "شماره کارت نامعتبر است";
        return "";
      case 'trackingNumber':
        if (!value) return "کد پیگیری الزامی است";
        if (!/^\d{4,12}$/.test(value)) return "کد پیگیری باید 4-12 رقم باشد";
        return "";
      default: return "";
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: validateField(name, value) }));
  };

  const handleC2CChange = (e) => {
    const { name, value } = e.target;
    let sanitized = value;
    if (name === 'cardNumber') sanitized = value.replace(/\D/g, '').slice(0, 16);
    if (name === 'trackingNumber') sanitized = value.replace(/\D/g, '').slice(0, 12);
    if (name === 'paymentDate') sanitized = value.replace(/[^0-9/]/g, '').slice(0, 10);

    setC2cData(prev => ({ ...prev, [name]: sanitized }));
    setErrors(prev => ({ ...prev, [name]: validateC2C(name, sanitized) }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!/^image\/(jpg|jpeg|png|gif|webp|bmp)$/.test(file.type)) {
        setErrors(prev => ({ ...prev, slip: "فقط تصویر مجاز است" }));
        return;
      }
      setSlipFile(file);
      setPaymentSlipUrl(URL.createObjectURL(file));
      setErrors(prev => ({ ...prev, slip: "" }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage("");

    const mainErrors = {};
    Object.keys(formData).forEach(key => {
      const err = validateField(key, formData[key]);
      if (err) mainErrors[key] = err;
    });

    // اگه کارت‌به‌کارته، چک کن
    if (formData.payment_method === "card_to_card") {
      ['paymentDate', 'cardNumber', 'trackingNumber'].forEach(key => {
        const err = validateC2C(key, c2cData[key]);
        if (err) mainErrors[key] = err;
      });
    }

    setErrors(mainErrors);
    if (Object.keys(mainErrors).length > 0) {
      setMessage("لطفاً خطاها را برطرف کنید");
      return;
    }

    setIsLoading(true);

    try {
      const fd = new FormData();
      fd.append('user_id', formData.user_id);
      fd.append('course_id', formData.course_id);
      fd.append('status', formData.status);
      fd.append('price', formData.price);
      fd.append('service_type', formData.service_type);
      fd.append('payment_method', formData.payment_method);

      // اگه کارت‌به‌کارت بود
      if (formData.payment_method === "card_to_card") {
        const descObj = {
          paymentDate: c2cData.paymentDate,
          cardNumber: c2cData.cardNumber,
          trackingNumber: c2cData.trackingNumber,
          note: c2cData.note
        };
        fd.append('description', JSON.stringify(descObj));
        if (slipFile) fd.append('paymentSlip', slipFile);
      }
      // اگه آنلاین بود، description رو نزن (زرین‌پال خودش مینویسه)

      const res = await fetch(`${BASE_URL}/orders/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'CSRF-Token': csrfToken,
        },
        credentials: 'include',
        body: fd,
      });

      if (!res.ok) throw new Error('خطا در بروزرسانی');
      setMessage('✅ سفارش بروزرسانی شد');
      setTimeout(() => navigate('/admin/orders'), 1500);
    } catch (err) {
      setMessage('❌ ' + String(err));
    } finally {
      setIsLoading(false);
    }
  };

  if (fetchLoading) return <div>در حال بارگذاری...</div>;

  return (
    <div className="edit-order-container">
      <h2>ویرایش سفارش</h2>
      
      {message && (
        <div className={message.includes("✅") ? "success-message" : "error-message"}>
          {message}
        </div>
      )}

      <form onSubmit={handleSubmit} noValidate>
        {/* کاربر */}
        <div className="form-group">
          <label>کاربر *</label>
          <select name="user_id" value={formData.user_id} onChange={handleChange}>
            <option value="">— انتخاب کنید —</option>
            {users.map(u => (
              <option key={u.id} value={u.id}>
                {u.username} ({u.first_name} {u.last_name})
              </option>
            ))}
          </select>
          {errors.user_id && <div className="text-danger">{errors.user_id}</div>}
        </div>

        {/* دوره */}
        <div className="form-group">
          <label>دوره *</label>
          <select name="course_id" value={formData.course_id} onChange={handleChange}>
            <option value="">— انتخاب کنید —</option>
            {courses.map(c => (
              <option key={c.id} value={c.id}>{c.title}</option>
            ))}
          </select>
          {errors.course_id && <div className="text-danger">{errors.course_id}</div>}
        </div>

        {/* وضعیت */}
        <div className="form-group">
          <label>وضعیت</label>
          <select name="status" value={formData.status} onChange={handleChange}>
            <option value="pending">در انتظار</option>
            <option value="paid">پرداخت شده</option>
            <option value="completed">تکمیل شده</option>
            <option value="canceled">لغو شده</option>
          </select>
        </div>

        {/* مبلغ */}
        <div className="form-group">
          <label>مبلغ (تومان) *</label>
          <input name="price" type="number" value={formData.price} onChange={handleChange} />
          {errors.price && <div className="text-danger">{errors.price}</div>}
        </div>

        {/* روش پرداخت (غیرقابل تغییر) */}
        <div className="form-group">
          <label>روش پرداخت</label>
          <input 
            type="text" 
            value={formData.payment_method === 'online' ? 'آنلاین (زرین‌پال)' : 'کارت به کارت'} 
            disabled 
            style={{ background: '#f5f5f5' }}
          />
        </div>

        {/* فیلدهای کارت‌به‌کارت */}
        {formData.payment_method === 'card_to_card' && (
          <>
            <hr />
            <h4>اطلاعات کارت‌به‌کارت</h4>

            <div className="form-group">
              <label>تاریخ پرداخت *</label>
              <input
                name="paymentDate"
                value={c2cData.paymentDate}
                onChange={handleC2CChange}
                placeholder={jalaliToday}
                maxLength={10}
              />
              {errors.paymentDate && <div className="text-danger">{errors.paymentDate}</div>}
            </div>

            <div className="form-group">
              <label>شماره کارت *</label>
              <input
                name="cardNumber"
                value={c2cData.cardNumber}
                onChange={handleC2CChange}
                maxLength={16}
              />
              {errors.cardNumber && <div className="text-danger">{errors.cardNumber}</div>}
            </div>

            <div className="form-group">
              <label>کد پیگیری *</label>
              <input
                name="trackingNumber"
                value={c2cData.trackingNumber}
                onChange={handleC2CChange}
                maxLength={12}
              />
              {errors.trackingNumber && <div className="text-danger">{errors.trackingNumber}</div>}
            </div>

            <div className="form-group">
              <label>یادداشت</label>
              <textarea
                name="note"
                value={c2cData.note}
                onChange={handleC2CChange}
              />
            </div>

            <div className="form-group">
              <label>فیش پرداخت</label>
              <input type="file" accept="image/*" onChange={handleFileChange} />
              {errors.slip && <div className="text-danger">{errors.slip}</div>}
              {paymentSlipUrl && (
                <div style={{ marginTop: 10 }}>
                  <img src={`${BASE_URL}${paymentSlipUrl}`} alt="فیش" style={{ maxWidth: 250 }} />
                </div>
              )}
            </div>
          </>
        )}

        {/* نمایش اطلاعات زرین‌پال */}
        {formData.payment_method === 'online' && zarinpalData && (
          <>
            <hr />
            <h4>اطلاعات پرداخت آنلاین</h4>
            <div style={{ background: '#f9f9f9', padding: 15, borderRadius: 5 }}>
              <p><strong>کد تراکنش:</strong> {zarinpalData.ref_id}</p>
              <p><strong>شماره کارت:</strong> {zarinpalData.card_pan}</p>
              {zarinpalData.order_id && <p><strong>شناسه سفارش:</strong> {zarinpalData.order_id}</p>}
              <p><strong>پیام:</strong> {zarinpalData.message}</p>
            </div>
          </>
        )}

        <div className="form-actions">
          <button type="submit" disabled={isLoading}>
            {isLoading ? 'در حال ارسال...' : 'بروزرسانی سفارش'}
          </button>
          <button type="button" onClick={() => navigate("/admin/orders")}>
            انصراف
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditOrder;
